import { Avatar, Badge, IconButton, makeStyles, TypographyProps, useTheme } from "@material-ui/core";
import DoneIcon from "@material-ui/icons/Done";
import LyfeTooltip from "../LyfeTooltip/LyfeTooltip";
import Typography, { LyfeTypographyTypes } from "../Typography/Typography";

interface StyleProps {
	style?: React.CSSProperties;
	className?: string;
}
export interface LyfeAvatarProps {
	firstName?: string;
	lastName?: string;
	currentUser?: boolean;
	tooltipProps?: StyleProps & { open?: boolean };
	avatarProps?: StyleProps;
	hideTooltip?: boolean;
	toolTipTitle?: string;
	avatarStyle?: React.CSSProperties;
	onClick?: () => void;
	initialsProps?: TypographyProps & LyfeTypographyTypes;
	src?: string;
	size?: SizeOptions;
	iconButtonProps?: {
		cursor?: string;
		disableRipple?: boolean;
	};
	isInsideButton?: boolean;
	hasBadge?: boolean;
	badgeColor?: string;
}

type SizeOptions = "XSmall" | "small" | "medium" | "large";

const LyfeAvatar: React.FC<LyfeAvatarProps> = ({
	firstName = "",
	lastName = "",
	currentUser,
	tooltipProps,
	avatarProps,
	hideTooltip,
	avatarStyle,
	toolTipTitle,
	onClick,
	src,
	initialsProps,
	size = "medium",
	iconButtonProps,
	isInsideButton,
	hasBadge,
	badgeColor,
	children,
}) => {
	const { palette } = useTheme();
	const fnInitial = firstName.charAt(0);
	const initials = `${fnInitial}${lastName.charAt(0)}`;
	const getAlphaVal = (s: any) => s.toLowerCase().charCodeAt(0) - 97 + 1;

	const handleClick = () => {
		onClick && onClick();
	};

	const getSize = (avatarSize: SizeOptions) => {
		switch (avatarSize) {
			case "XSmall":
				return "24px";
			case "small":
				return "32px";
			case "medium":
				return "44px";
			case "large":
				return "80px";
			default:
				return "44px";
		}
	};

	const getInitialsSize = (avatarSize: SizeOptions) => {
		switch (avatarSize) {
			case "XSmall":
				return "labels";
			case "small":
				return "labels";
			default:
				return "mainText";
		}
	};

	const getColors = () => {
		if (currentUser) return [palette.warning.dark, palette.warning.light];

		const alphaValue = getAlphaVal(fnInitial);

		if (alphaValue < 5) return ["#7C4595", "#EDE4F1"];
		if (alphaValue > 5 && alphaValue < 10) return ["#614FAA", "#DADDFE"];
		if (alphaValue > 10 && alphaValue < 15) return ["#4C7057", "#E8F3D9"];
		if (alphaValue > 15 && alphaValue < 20) return ["#A87511", "#FFF4DC"];
		return ["#007876", "#CCF6F2"];
	};

	const classes = useStyles({ badgeColor });

	return (
		<LyfeTooltip {...tooltipProps} disableHoverListener={hideTooltip} title={toolTipTitle ? toolTipTitle : `${firstName} ${lastName}`}>
			{isInsideButton ? (
				// currently supports one type of badge with tick icon
				<Badge
					classes={{ badge: classes["badge"] }}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "right",
					}}
					invisible={!hasBadge}
					badgeContent={<DoneIcon style={{ width: 6, height: 6 }} color="secondary" />}
					overlap="circular"
					style={{ zIndex: 0 }}
				>
					<Avatar
						src={src}
						style={{
							backgroundColor: getColors()[1],
							border: `1px solid ${getColors()[0]}`,
							color: getColors()[0],
							width: getSize(size),
							height: getSize(size),
							...avatarStyle,
						}}
						{...avatarProps}
					>
						{children || (
							<Typography type={getInitialsSize(size)} {...initialsProps}>
								{initials}
							</Typography>
						)}
					</Avatar>
				</Badge>
			) : (
				<IconButton
					onClick={handleClick}
					style={{ padding: 0, cursor: iconButtonProps?.cursor }}
					disableRipple={iconButtonProps?.disableRipple}
				>
					<Badge
						classes={{ badge: classes["badge"] }}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "right",
						}}
						invisible={!hasBadge}
						badgeContent={<DoneIcon style={{ width: 6, height: 6 }} color="secondary" />}
						overlap="circular"
					>
						<Avatar
							src={src}
							style={{
								backgroundColor: getColors()[1],
								border: `1px solid ${getColors()[0]}`,
								color: getColors()[0],
								width: getSize(size),
								height: getSize(size),
								...avatarStyle,
							}}
							{...avatarProps}
						>
							{children || (
								<Typography type={getInitialsSize(size)} {...initialsProps}>
									{initials}
								</Typography>
							)}
						</Avatar>
					</Badge>
				</IconButton>
			)}
		</LyfeTooltip>
	);
};

export default LyfeAvatar;

const useStyles = makeStyles<any, { badgeColor: string | undefined }>({
	badge: {
		height: "10px",
		minWidth: "10px",
		padding: 0,
		zIndex: 0,
		backgroundColor: ({ badgeColor }) => badgeColor,
	},
});
